import { Component, EventEmitter, forwardRef, Input, ViewChild } from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";

@Component({
  selector: "core-ui-input-text",
  templateUrl: "./input-text.component.html",
  styleUrls: ["./input-text.component.scss"],
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputTextComponent),
      multi: true
    }
  ]
})
export class InputTextComponent implements ControlValueAccessor {
  @Input() label = "";
  @Input() placeholder = "";
  @Input() disabled = false;
  @Input() value: string | null = null;
  @Input() type: "text" | "password" = "text";
  @Input() variant: "default" | "login" | "search-large" = "default";
  @Input() iconRight?: string;
  @Input() clearable: boolean = false;
  @Input() errorMessage: string | null = null;
  @Input() helperMessage: string | null = null;

  @ViewChild('inputRef', { static: false })
  inputRef!: any;

  showPassword = false;

  private onChange: (v: string | null) => void = () => { };
  private onTouched: () => void = () => { };

  writeValue(obj: string | null): void {
    this.value = obj ?? "";
  }

  registerOnChange(fn: (v: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  handleValueChange(v: string) {
    this.value = v;
    this.onChange(v);
  }

  handleBlur() {
    this.onTouched();
  }

  get iconRightSrc(): string {
    if (this.type === "password") {
      const name = this.showPassword ? "Vector" : this.iconRight || "eye";
      return `assets/core-ui/icons/${name}.svg`;
    }
    const name = this.iconRight ?? "";
    return `assets/core-ui/icons/${name}.svg`;
  }

  get iconRightAlt(): string {
    return this.iconRight ?? "icon";
  }

  onRightIconClick(): void {
    if (this.type === "password") {
      this.showPassword = !this.showPassword;
    }
  }

  get showClearIcon(): boolean {
    const val = (this.value ?? "").trim();
    return this.clearable && val.length > 0;
  }

  get displayMessage(): string | null {
    return this.errorMessage ?? this.helperMessage;
  }

  get hasError(): boolean {
    return !!this.errorMessage;
  }

  onIconMouseDown(event: MouseEvent) {
    event.preventDefault();
  }

  clearValue(event: MouseEvent) {
    event.stopPropagation();
    this.handleValueChange('');
    if (this.inputRef?.focus) {
      this.inputRef.focus();
    }
  }
}
